import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormAttachmentUploadRequirements } from '../attachments/form-attachment-requirements';
import { GeneralFormData } from '../general-form-data.model';
import { FormStep } from '../form-steps.interface';
import { parseLocalDate } from '../../../utils/date-time-formatter';
import { FormAccessControl } from '../../form-registry';


export class PfizerPreTaskSafetyPlan {
    id?: string;
    submittedByName: string = '';
    submittedByEmail: string = '';
    submissionDate?: Date;
    subcontractor: string = '';
    jobName: string = '';
    jobNumber: string = '';
    taskDescription: string = '';
    date: Date | null = null;
    
    // Job analysis individual fields (instead of arrays)
    jobSteps: string = '';
    identifiedHazard1: string = '';
    identifiedHazard2: string = '';
    identifiedHazard3: string = '';
    identifiedHazard4: string = '';
    identifiedHazard5: string = '';
    identifiedHazard6: string = '';
    identifiedHazard7: string = '';

    hazardControl1: string = '';
    hazardControl2: string = '';
    hazardControl3: string = '';
    hazardControl4: string = '';
    hazardControl5: string = '';
    hazardControl6: string = '';
    hazardControl7: string = '';

    
    // Required permits - individual boolean properties
    confinedSpacePermit!: boolean;
    hotWorkPermit!: boolean;
    lockoutTagoutPermit!: boolean;
    otherPermit!: boolean;
    otherPermitDescription: string = '';
    
    // Hazard assessment radio buttons
    fireExplosionToxicHazard: string = '';
    sdsReviewRequired: string = '';
    
    // Hazard conditions - individual boolean properties
    contactingTemperatureExtremes!: boolean;
    contactingElectricalCurrent!: boolean;
    environmentalAirborneRelease!: boolean;
    movingObjectsEquipment!: boolean;
    hazardousSubstance!: boolean;
    obstructionInterference!: boolean;
    struckBy!: boolean;
    fallSlipTrip!: boolean;
    materialHandling!: boolean;
    stuckAgainst!: boolean;
    caughtInBetween!: boolean;
    otherHazardConditions!: boolean;
    otherConditions: string = '';
    
    // Control methods - individual boolean properties
    ventilationOfExposureArea!: boolean;
    isolationOfHazardFromWorker!: boolean;
    substitutionOfHazardWithLessSevereOne!: boolean;
    eliminationOfHazard!: boolean;
    changeOfWorkMethods!: boolean;
    goodWorkPractices!: boolean;
    personalProtectiveEquipment!: boolean;
    otherControlMethods!: boolean;
    otherControlMethod: string = '';
    
    // Crew member information
    forepersonFirstName: string = '';
    forepersonLastName: string = '';
    forepersonSignature: Array<string> = [];
    crewMember1FirstName: string = '';
    crewMember1LastName: string = '';
    crewMember1Signature: Array<string> = [];
    crewMember2FirstName: string = '';
    crewMember2LastName: string = '';
    crewMember2Signature: Array<string> = [];
    crewMember3FirstName: string = '';
    crewMember3LastName: string = '';
    crewMember3Signature: Array<string> = [];

    otherNotes: string = '';



    constructor(data?: Partial<PfizerPreTaskSafetyPlan>) {
        // Set default date to today if not provided
        if (data) {
            Object.assign(this, {
                ...data,
                date: data.date ? parseLocalDate(data.date as any) : null,
                submissionDate: data.submissionDate ? new Date(data.submissionDate) : null
            });
        }
    }

    static getFormSteps(): FormStep[] {
        return [
            {
                id: 'job-details',
                shortTitle: 'Job Details',
                title: 'Job Details & Task Description',
                icon: 'business-outline',
                description: 'Enter job information and task details',
                sections: [
                    {
                        fields: ['subcontractor', 'date', 'jobName', 'jobNumber', 'taskDescription']
                    }
                ]
            },
            {
                id: 'job-analysis',
                shortTitle: 'Job Analysis',
                title: 'Job Steps & Hazard Analysis',
                icon: 'list-outline',
                description: 'Analyze job steps and identify hazards',
                sections: [
                    {
                        fields: ['jobSteps']
                    },
                    {
                        title: 'Identified Hazards & Hazard Controls',
                        description: 'Please fill out as many hazards as needed to properly identify and control all potential risks for this task. At least one hazard is required.',
                        subtitle: 'Hazard 1',
                        fields: ['identifiedHazard1', 'hazardControl1']
                    },
                    {
                        subtitle: 'Hazard 2',
                        fields: ['identifiedHazard2', 'hazardControl2']
                    },
                    {
                        subtitle: 'Hazard 3',
                        fields: ['identifiedHazard3', 'hazardControl3']
                    },
                    {
                        subtitle: 'Hazard 4',
                        fields: ['identifiedHazard4', 'hazardControl4']
                    },
                    {
                        subtitle: 'Hazard 5',
                        fields: ['identifiedHazard5', 'hazardControl5']
                    },
                    {
                        subtitle: 'Hazard 6',
                        fields: ['identifiedHazard6', 'hazardControl6']
                    },
                    {
                        subtitle: 'Hazard 7',
                        fields: ['identifiedHazard7', 'hazardControl7']
                    }
                  
                ]
            },
            {
                id: 'permits-hazards',
                shortTitle: 'Permits & Hazards',
                title: 'Required Permits & Hazard Assessment',
                icon: 'warning-outline',
                description: 'Identify required permits and assess hazards',
                sections: [
                    {
                        checkboxGroups: [
                            {
                                fields: ['confinedSpacePermit', 'hotWorkPermit', 'lockoutTagoutPermit', 'otherPermit'],
                                checkboxLabel: 'What permits are required for this task?'
                            }
                        ],
                        fields: ['otherPermitDescription', 'fireExplosionToxicHazard', 'sdsReviewRequired']
                    }
                ]
            },
            {
                id: 'hazard-evaluation',
                shortTitle: 'Hazard Evaluation',
                title: 'Hazard Conditions & Control Methods',
                icon: 'shield-checkmark-outline',
                description: 'Evaluate hazard conditions and control methods',
                sections: [
                    {
                        checkboxGroups: [
                            {
                                fields: ['contactingTemperatureExtremes', 'contactingElectricalCurrent', 'environmentalAirborneRelease', 'movingObjectsEquipment', 'hazardousSubstance', 'obstructionInterference', 'struckBy', 'fallSlipTrip', 'materialHandling', 'stuckAgainst', 'caughtInBetween', 'otherHazardConditions'],
                                checkboxLabel: this.getFieldMappings()['hazardConditions']
                            },
                            {
                                fields: ['ventilationOfExposureArea', 'isolationOfHazardFromWorker', 'substitutionOfHazardWithLessSevereOne', 'eliminationOfHazard', 'changeOfWorkMethods', 'goodWorkPractices', 'personalProtectiveEquipment', 'otherControlMethods'],
                                checkboxLabel: this.getFieldMappings()['controlMethods']
                            }
                        ],
                        fields: ['otherConditions', 'otherControlMethod']
                    }
                ]
            },
            {
                id: 'crew-signatures',
                shortTitle: 'Crew Signatures',
                title: 'Crew Member Information & Signatures',
                icon: 'pencil-outline',
                description: 'Record crew information and obtain signatures',
                sections: [
                    {
                        subtitle: 'Foreperson',
                        fields: ['forepersonFirstName', 'forepersonLastName', 'forepersonSignature']
                    },
                    {
                        subtitle: 'Crew Member 1',
                        fields: ['crewMember1FirstName', 'crewMember1LastName', 'crewMember1Signature']
                    },
                    {
                        subtitle: 'Crew Member 2 (Optional)',
                        fields: ['crewMember2FirstName', 'crewMember2LastName', 'crewMember2Signature']
                    },
                    {
                        subtitle: 'Crew Member 3 (Optional)',
                        fields: ['crewMember3FirstName', 'crewMember3LastName', 'crewMember3Signature']
                    },
                    {
                        fields: ['otherNotes']
                    }
                ]
            }
        ];
    }

    static formAccessControl(): FormAccessControl {
        return {
            requiresPfizerAccess: true,
        };
    }

    static getFormInfo(): GeneralFormData {
        return {
            formType: 'pfizer-pre-task-safety-plan',
            formTitle: 'Pfizer Pre-Task Safety Plan',
            formIcon: 'information-circle-outline',
            cardImage: 'assets/form-card-tiles/pfizer-pre-task-safety-plan.png',
            formAttachmentRequirements: {
                signatures: [
                    {
                        identifier: 'forepersonSignature',
                        label: this.getFieldMappings()['forepersonSignature'],
                        required: true,
                      },
                      {
                        identifier: 'crewMember1Signature',
                        label: this.getFieldMappings()['crewMember1Signature'],
                        required: false,
                      },
                      {
                        identifier: 'crewMember2Signature',
                        label: this.getFieldMappings()['crewMember2Signature'],
                        required: false,
                      },
                      {
                        identifier: 'crewMember3Signature',
                            label: this.getFieldMappings()['crewMember3Signature'],
                        required: false,
                      }
                    ]
            }
        };
    }

    static getFieldMappings(): Record<string, string> {
        return {
            submittedByName: "Submitter Name",
            submittedByEmail: "Submitter Email",
            submissionDate: "Submission Date",
            subcontractor: "Subcontractor",
            jobName: "Job Name",
            jobNumber: "Job Number",
            taskDescription: "Task Description",
            date: "Date",
            jobSteps: "List all the steps of the job",
            identifiedHazards: "Identified Hazards & Hazard Controls*",
            identifiedHazardsDescription: "Please fill out as many hazards as needed to properly identify and control all potential risks for this task. At least one hazard is required.",
            identifiedHazard1: "Identified Hazard",
            identifiedHazard2: "Identified Hazard",
            identifiedHazard3: "Identified Hazard",
            identifiedHazard4: "Identified Hazard",
            identifiedHazard5: "Identified Hazard",
            identifiedHazard6: "Identified Hazard",
            identifiedHazard7: "Identified Hazard",
  
            hazardControls: "How will you control the hazards?",
            hazardControl1: "Hazard Control",
            hazardControl2: "Hazard Control",
            hazardControl3: "Hazard Control",
            hazardControl4: "Hazard Control",
            hazardControl5: "Hazard Control",
            hazardControl6: "Hazard Control",
            hazardControl7: "Hazard Control",

            requiredPermits: "What permits are required for this task?",
            confinedSpacePermit: "Confined Space",
            hotWorkPermit: "Hot Work",
            lockoutTagoutPermit: "Lockout/Tagout",
            otherPermit: "Other",
            otherPermitDescription: "Other Permit Description",
            fireExplosionToxicHazard: "Is there a potential fire, explosion, toxic or radioactive release hazard?",
            sdsReviewRequired: "Are there any SDS's that might need review for hazardous substances that might be present on the job site?",
            hazardConditions: "Use the following categories to assist you in a proper evaluation of all the hazards that have been identified. Can any of the following conditions occur with the hazards that have been identified?",
            contactingTemperatureExtremes: "Contacting Temperature Extremes",
            contactingElectricalCurrent: "Contacting Electrical Current",
            environmentalAirborneRelease: "Environmental/Airborne Release",
            movingObjectsEquipment: "Moving Objects/Equipment",
            hazardousSubstance: "Hazardous Substance",
            obstructionInterference: "Obstruction/Interference",
            struckBy: "Struck By",
            fallSlipTrip: "Fall/Slip/Trip",
            materialHandling: "Material Handling",
            stuckAgainst: "Stuck Against",
            caughtInBetween: "Caught In/Between",
            otherHazardConditions: "Other",
            otherConditions: "Other Conditions",
            controlMethods: "Use the following categories to assist you in determining the proper control methods for all the hazards that have been identified and evaluated.",
            ventilationOfExposureArea: "Ventilation of exposure area",
            isolationOfHazardFromWorker: "Isolation of hazard from worker",
            substitutionOfHazardWithLessSevereOne: "Substitution of hazard with less severe one",
            eliminationOfHazard: "Elimination of hazard",
            changeOfWorkMethods: "Change of work methods",
            goodWorkPractices: "Good work practices",
            personalProtectiveEquipment: "Personal protective equipment",
            otherControlMethods: "Other",
            otherControlMethod: "Other Control Method",
            forepersonFirstName: "Foreperson Name (First)",
            forepersonLastName: "Foreperson Name (Last)",
            forepersonSignature: "Foreperson Signature",
            crewMember1FirstName: "Crew Member Name (First)",
            crewMember1LastName: "Crew Member Name (Last)",
            crewMember1Signature: "Crew Member Signature",
            crewMember2FirstName: "Crew Member Name (First)",
            crewMember2LastName: "Crew Member Name (Last)",
            crewMember2Signature: "Crew Member Signature",
            crewMember3FirstName: "Crew Member Name (First)",
            crewMember3LastName: "Crew Member Name (Last)",
            crewMember3Signature: "Crew Member Signature",
            otherNotes: "Other Notes",

        };
    }
 
} 